import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import classes from '../../../pipeline.module.scss';
import Api from '../../../../../../../api/api';
import Table from '../../../../../../_common/Tables/Table';
import { BarChart, Card, SectionHead } from '../../PipelineComponents';
import {
  formattedCount, formattedDays, formattedPercent, funnelStage,
} from '../../../pipelineHelpers';
import ProcessingTimesCard from './ProcessingTimesCard';

const methodsTableHeader = [
  { name: 'Method', path: 'name' },
  { name: 'Shipments', path: 'shipments' },
  { name: 'Avg transit', path: 'transit' },
  { name: 'On time', path: 'onTime' },
];

const FUNNEL_TIPS = {
  processed: {
    text: 'Prescriptions the pharmacy has worked on.',
    steps: ['The 100% baseline every step below is measured against.'],
    note: 'Because this is the top of the funnel, a prescription received but never processed is invisible'
      + ' here.',
  },
  shipped: {
    text: 'Prescriptions that have gone out.',
    steps: ['Counted from the ship date on the prescription.'],
    note: 'Some of the drop from processed will be pickups that never ship, but a large fall is usually the'
      + ' ship date not being recorded rather than work not happening.',
    warn: true,
  },
  delivered: {
    text: 'Prescriptions confirmed as delivered.',
    steps: ['Counted from the delivery date on the prescription.'],
    note: 'If this matches shipped exactly, delivery is being inferred from shipping rather than confirmed.'
      + ' Until a real delivery timestamp is captured, this step adds nothing to the one above it.',
    warn: true,
  },
};

const ProcessingShipping = ({ section, params }) => {
  const firstLoad = useRef(true);

  const [summary, setSummary] = useState({});
  const [loading, loadingToggle] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        loadingToggle(true);

        const { data } = await Api.getProcessingTimesSummary(params);

        setSummary(data?.data || {});

        loadingToggle(false);
        firstLoad.current = false;
      } catch (e) {
        console.warn(e);
      }
    })();
  }, [params]);

  const {
    stages, cycleTimes, quality, methods,
  } = useMemo(() => {
    const funnel = summary?.funnel;
    const cycle = summary?.cycleTimes;

    return {
      stages: funnel
        ? [
          funnelStage('Processed', funnel.processed, funnel.processedPct, FUNNEL_TIPS.processed),
          funnelStage('Shipped', funnel.shipped, funnel.shippedPct, FUNNEL_TIPS.shipped),
          funnelStage('Delivered', funnel.delivered, funnel.deliveredPct, FUNNEL_TIPS.delivered),
        ]
        : [],

      cycleTimes: [
        { label: 'Received → processed', value: formattedDays(cycle?.avgReceivedToProcessed) },
        { label: 'Processed → shipped', value: formattedDays(cycle?.avgProcessedToShipped) },
        { label: 'Shipped → delivered', value: formattedDays(cycle?.avgShippedToDelivered) },
        { label: 'Received → delivered', value: formattedDays(cycle?.avgReceivedToDelivered), strong: true },
      ],

      quality: [
        { label: 'Same-day processing', value: formattedPercent(funnel?.sameDayProcessingPct) },
        { label: 'On-time ship rate', value: formattedPercent(cycle?.onTimeShipRate) },
        { label: 'Shipments delayed > 5 d', value: formattedCount(funnel?.delayedOver5d) },
      ],

      methods: (summary?.byMethod || []).map((d) => ({
        name: d.deliveryMethod,
        shipments: formattedCount(d.shipments),
        transit: formattedDays(d.avgTransitDays),
        onTime: formattedPercent(d.onTimePct),
      })),
    };
  }, [summary]);

  return (
    <>
      <SectionHead section={section} />

      <div className={`${classes.row} ${classes.row_2}`}>
        <Card
          title="Fulfillment funnel"
          caption="Prescriptions received in the period"
          loading={loading}
          firstLoad={firstLoad.current}
          isEmpty={!stages.length}
        >
          <BarChart data={stages} />
        </Card>

        <ProcessingTimesCard params={params} />
      </div>

      <div className={`${classes.row} ${classes.row_2}`}>
        <Card
          title="By ship method"
          caption="Shipments in the period"
          loading={loading}
          firstLoad={firstLoad.current}
          isEmpty={!methods.length}
        >
          <div className={`${classes.pipeline_table} ${classes.bordered}`}>
            <Table
              header={methodsTableHeader}
              data={methods}
            />
          </div>

          <p className={classes.note}>
            <b>Answer:</b>
            {' '}
            the method is on the script, so the volume split is already reportable. Transit time and
            on-time rate stay empty until a delivery timestamp is recorded against the shipment.
          </p>
        </Card>

        <Card
          title="Stage cycle times"
          caption="Average days between timestamps"
          loading={loading}
          firstLoad={firstLoad.current}
        >
          <div className={`${classes.kv_list} ${classes.bordered}`}>
            {cycleTimes.map((item) => (
              <div className={classes.kv} key={item.label}>
                {item.strong ? <b>{item.label}</b> : item.label}

                <span className={classes.kv_value}>{item.value}</span>
              </div>
            ))}
          </div>

          <div className={`${classes.kv_list} ${classes.bordered}`}>
            {quality.map((item) => (
              <div className={classes.kv} key={item.label}>
                {item.label}

                <span className={classes.kv_value}>{item.value}</span>
              </div>
            ))}
          </div>

          <p className={classes.note}>
            <span className={`${classes.badge} ${classes.badge_have}`}>mostly in place</span>

            {' '}

            <code>Processed On</code>
            ,
            {' '}
            <code>Ship On</code>
            {' '}
            and
            {' '}
            <code>ETA</code>
            {' '}
            already exist - delivery confirmation is the timestamp to add, and until it lands every
            stage that ends in a delivery reads as no data.
          </p>
        </Card>
      </div>
    </>
  );
};

ProcessingShipping.propTypes = {
  section: PropTypes.object.isRequired,
  params: PropTypes.object.isRequired,
};

export default ProcessingShipping;
